// CommentSection.jsx

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  memo,
} from 'react';
import { FaPaperPlane, FaThumbsUp, FaReply } from 'react-icons/fa';
import { createTempComment } from '../utils/postUtils';
import styles from './CommentSection.module.css';
import { FaTrash } from "react-icons/fa";
import { useDeleteCommentMutation } from '../store/api/postApi';
const COMMENTS_INCREMENT = 3;
const REPLIES_INCREMENT = 3; // How many replies to show at a time

/* ───────────────────────────────────────── */
/* Helpers */
/* ───────────────────────────────────────── */

function getInitials(name = '') {
  return name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function formatCommentDate(dateString) {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    const diffWk = Math.floor(diffDay / 7);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m`;
    if (diffHr < 24) return `${diffHr}h`;
    if (diffDay < 7) return `${diffDay}d`;
    if (diffWk < 4) return `${diffWk}w`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

/* ───────────────────────────────────────── */
/* Avatar */
/* ───────────────────────────────────────── */

function Avatar({ user, size = 'md' }) {
  const [imgError, setImgError] = useState(false);

  if (user?.profilePhotoUrl && !imgError) {
    return (
      <img
        src={user.profilePhotoUrl}
        alt={user?.name || 'User'}
        className={`${styles.avatar} ${styles[`avatar_${size}`]}`}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div className={`${styles.avatarFallback} ${styles[`avatar_${size}`]}`}>
      {getInitials(user?.name)}
    </div>
  );
}

/* ───────────────────────────────────────── */
/* Comment Input */
/* ───────────────────────────────────────── */

function CommentInput({
  placeholder = 'Add a comment…',
  onSubmit,
  isDisabled,
  autoFocus = false,
}) {
  const [text, setText] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed || isDisabled) return;
    onSubmit(trimmed);
    setText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={styles.inputRow}>
      <input
        ref={inputRef}
        type="text"
        className={styles.input}
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
      />
      <button
        className={styles.submitBtn}
        onClick={handleSubmit}
        disabled={!text.trim() || isDisabled}
        aria-label="Post"
      >
        <FaPaperPlane />
      </button>
    </div>
  );
}

/* ───────────────────────────────────────── */
/* Comment Item */
/* ───────────────────────────────────────── */

const CommentItem = memo(function CommentItem({
  comment,
  onSubmitComment,
  currentUser,
  postOwnerId,
  postId,
  depth = 0,
}) {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [localReplies, setLocalReplies] = useState(comment.replies || []);
  const [showReplies, setShowReplies] = useState(true);
  const [deleteComment] = useDeleteCommentMutation();
  const [isDeleted, setIsDeleted] = useState(false);
  const currentUserId =
  currentUser?.userId ||
  currentUser?.id ||
  currentUser?.user?.id;
  // Replies are initially hidden; first click reveals REPLIES_INCREMENT replies
  const [visibleRepliesCount, setVisibleRepliesCount] = useState(0);

  // When new replies arrive (e.g. from parent prop changes), sync them
  useEffect(() => {
    setLocalReplies(comment.replies || []);
  }, [comment.replies]);
  console.log("PostOwnerID", postOwnerId);
  const handleDelete = async () => {
    try {
  
      setIsDeleted(true);
  
      await deleteComment({
        commentId: comment.id,
        postId: postId,
      }).unwrap();
  
    } catch (err) {
  
      setIsDeleted(false);
      console.error("Delete failed", err);
    }
  };

  const handleReplySubmit = async (text) => {
    const tempReply = createTempComment(text, currentUser);

    setLocalReplies((prev) => [...prev, tempReply]);
    setShowReplyInput(false);
    setShowReplies(true);

    // Make sure the newly added reply is visible
    setVisibleRepliesCount((prev) =>
      Math.max(prev, localReplies.length + 1)
    );

    try {
      const saved = await onSubmitComment(text, comment.id);

      if (saved?.id) {
        setLocalReplies((prev) =>
          prev.map((r) =>
            r.id === tempReply.id
              ? { ...saved, user: saved.user || tempReply.user }
              : r
          )
        );
      }
    } catch {
      setLocalReplies((prev) => prev.filter((r) => r.id !== tempReply.id));
    }
  };

  // ── Reply visibility helpers ──────────────────────────────────────
  const totalReplies = localReplies.length;
  const hasReplies = totalReplies > 0;

  // Replies are shown from the START of the array up to visibleRepliesCount
  const visibleReplies = useMemo(
    () => localReplies.slice(0, visibleRepliesCount),
    [localReplies, visibleRepliesCount]
  );

  const hiddenCount = totalReplies - visibleRepliesCount;
  const repliesAreCollapsed = visibleRepliesCount === 0;

  // Show initial batch of replies
  const handleShowReplies = () => {
    setVisibleRepliesCount(REPLIES_INCREMENT);
    setShowReplies(true);
  };

  // Load next batch
  const handleViewMoreReplies = () => {
    setVisibleRepliesCount((prev) =>
      Math.min(prev + REPLIES_INCREMENT, totalReplies)
    );
  };

  // Collapse all replies back
  const handleHideReplies = () => {
    setVisibleRepliesCount(0);
    setShowReplies(false);
  };

  const indentLevel = Math.min(depth, 2);
  if (isDeleted) return null;
  return (
    <div
      className={styles.commentThread}
      style={{ '--depth': indentLevel }}
    >
      <div className={styles.commentItem}>
        <div className={styles.avatarCol}>
          <Avatar user={comment.user} size={depth === 0 ? 'md' : 'sm'} />
          {hasReplies && !repliesAreCollapsed && (
            <div className={styles.threadLine} />
          )}
        </div>

        <div className={styles.commentBubbleCol}>
          <div className={styles.commentBubble}>
            <div className={styles.commentHeader}>
              <span className={styles.commentAuthor}>
                {comment?.user?.name ?? 'Unknown User'}
              </span>
              <span className={styles.commentDate}>
                {formatCommentDate(comment.createdAt)}
              </span>
            </div>
            <p className={styles.commentText}>{comment.commentText}</p>
          </div>

          <div className={styles.commentActions}>

            <button
              className={`${styles.commentActionBtn} ${
                showReplyInput ? styles.commentActionActive : ''
              }`}
              onClick={() => setShowReplyInput((p) => !p)}
            >
              <FaReply />
              <span>Reply</span>
            </button>
            {
  (currentUserId?.toString() === comment?.user?.id?.toString() ||
   currentUserId?.toString() === postOwnerId?.toString()) && (

    <button
      className={styles.commentActionBtn}
      onClick={handleDelete}
    >
    <FaTrash />
<span>Delete</span>
    </button>
  )
}
          </div>

          {showReplyInput && (
            <div className={styles.replyInputRow}>
              <Avatar user={currentUser} size="sm" />
              <CommentInput
                placeholder={`Reply to ${comment?.user?.name || 'user'}…`}
                onSubmit={handleReplySubmit}
                autoFocus
              />
            </div>
          )}

          {/* ── Replies section ────────────────────────────────────── */}
          {hasReplies && (
            <div className={styles.repliesWrapper}>

              {/* "View X replies" — shown when all replies are collapsed */}
              {repliesAreCollapsed && (
                <button
                  className={styles.viewRepliesBtn}
                  onClick={handleShowReplies}
                >
                  <span className={styles.viewRepliesLine} />
                  View {totalReplies}{' '}
                  {totalReplies === 1 ? 'reply' : 'replies'}
                </button>
              )}

              {/* Rendered replies */}
              {!repliesAreCollapsed && (
                <div className={styles.repliesList}>
                  {visibleReplies.map((reply) => (
                    <CommentItem
                    key={reply.id}
                    comment={reply}
                    onSubmitComment={onSubmitComment}
                    currentUser={currentUser}
                    postOwnerId={postOwnerId}
                    postId={postId}
                    depth={depth + 1}
                  />
                  ))}

                  {/* "View N more replies" */}
                  {hiddenCount > 0 && (
                    <button
                      className={styles.viewRepliesBtn}
                      onClick={handleViewMoreReplies}
                    >
                      <span className={styles.viewRepliesLine} />
                      View {Math.min(hiddenCount, REPLIES_INCREMENT)} more{' '}
                      {Math.min(hiddenCount, REPLIES_INCREMENT) === 1
                        ? 'reply'
                        : 'replies'}
                    </button>
                  )}

                  {/* "Hide replies" — collapse back */}
                  {hiddenCount === 0 && (
                    <button
                      className={`${styles.viewRepliesBtn} ${styles.hideRepliesBtn}`}
                      onClick={handleHideReplies}
                    >
                      <span className={styles.viewRepliesLine} />
                      Hide replies
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {/* ────────────────────────────────────────────────────────── */}
        </div>
      </div>
    </div>
  );
});

/* ───────────────────────────────────────── */
/* Comment Section */
/* ───────────────────────────────────────── */

export default function CommentSection({
  comments = [],
  onSubmitComment,
  isCommenting,
  currentUser,
  postOwnerId,
  postId
}) {
  const [localComments, setLocalComments] = useState([]);
  const [visibleCount, setVisibleCount] = useState(COMMENTS_INCREMENT);

  useEffect(() => {
    setLocalComments(comments);
    setVisibleCount(COMMENTS_INCREMENT);
  }, [comments]);

  const visibleComments = useMemo(() => {
    return localComments.slice(0, visibleCount);
  }, [localComments, visibleCount]);

  const handleTopLevelSubmit = useCallback(
    async (text) => {
      //const tempComment = createTempComment(text, currentUser);
      const tempComment = createTempComment(text, {
        id: currentUser?.userId,   // ✅ FIX
        name: currentUser?.name || "You",
        profilePhotoUrl: currentUser?.profilePhotoUrl
      });
      setLocalComments((prev) => [tempComment, ...prev]);

      try {
        const saved = await onSubmitComment(text, null);

        if (saved?.id) {
          setLocalComments((prev) =>
            prev.map((c) =>
              c.id === tempComment.id
                ? { ...saved, user: saved.user || tempComment.user }
                : c
            )
          );
        }
      } catch {
        setLocalComments((prev) =>
          prev.filter((c) => c.id !== tempComment.id)
        );
      }
    },
    [onSubmitComment, currentUser]
  );
 
  return (
    <div className={styles.commentSection}>
      {/* Input */}
      <div className={styles.replyInputRow}>
        <Avatar user={currentUser} size="md" />
        <CommentInput
          placeholder="Add a comment…"
          onSubmit={handleTopLevelSubmit}
          isDisabled={isCommenting}
        />
      </div>

      {/* Comments */}
      {visibleComments.length > 0 && (
        <div className={styles.commentList}>
          {visibleComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onSubmitComment={onSubmitComment}
              currentUser={currentUser}
              postOwnerId={postOwnerId}
              postId={postId}
            />
          ))}
        </div>
      )}

      {/* View More Button */}
      {visibleCount < localComments.length && (
        <button
          className={styles.loadMore}
          onClick={() =>
            setVisibleCount((prev) => prev + COMMENTS_INCREMENT)
          }
        >
          View{' '}
          {Math.min(COMMENTS_INCREMENT, localComments.length - visibleCount)}{' '}
          more comments
        </button>
      )}

      {localComments.length === 0 && (
        <p className={styles.emptyState}>Be the first to comment</p>
      )}
    </div>
  );
}